<script setup lang="ts">
import StatusBadge from '@/components/common/StatusBadge.vue'
import { usePolicies } from '@/composables/asicw/policies'
import { currencyFormatter } from '@/helpers/ep/formatters'
import { PolicyStatus, type IPolicy, type IPolicyFilters } from '@/types/asicw/policies'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { Icon } from '@iconify/vue'
import { useDebounceFn } from '@vueuse/core'
import { h, reactive, ref } from 'vue'
import ClaimCreateDialog from '@/components/asicw/claims/ClaimCreateDialog.vue'
import RlEmptyDataset from '@/components/common/RlEmptyDataset.vue'
import { useOIDCStore } from '@/stores/oidc.store'
import { downloadFile } from '@/util'
import { getPoliciesExportCSV } from '@/services/asicw/policies.service'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import { NotifyFail } from '@/helpers/core/notifications'

const glassIcon = h(Icon, { icon: 'mdi:search' })

const { getPoliciesQuery } = usePolicies()

const { isASICWAppUser, isASICWOperationalUser, isCustomer, isASICWViewerUser } = useOIDCStore()

const selection = defineModel<IPolicy[]>({ required: true })

const filters = reactive<IPolicyFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
})

const { data: policiesData, isError, isFetching, error, isRefetching, refetch } = getPoliciesQuery(filters)

const groupFragment = ref('')
const searchGroup = useDebounceFn(
  () => {
    filters.policy_group_number = groupFragment.value.length ? groupFragment.value : undefined
  },
  500,
  { maxWait: 3000 }
)

const lookupFragment = ref('')
const searchFragment = useDebounceFn(
  () => {
    filters.fragment = lookupFragment.value.length ? lookupFragment.value : undefined
  },
  500,
  { maxWait: 3000 }
)

const onSelect = (updatedSelection: IPolicy[]) => {
  selection.value = updatedSelection
}

const onCustomerChange = (customer: any) => {
  filters.customer_id = customer?.id
}

const isActive = (policy: IPolicy) => policy.status == PolicyStatus.Active

const showClaimDialog = ref(false)
const selectedPolicy = ref<IPolicy>()

const handleClaim = (policy: IPolicy) => {
  selectedPolicy.value = policy
  showClaimDialog.value = true
}

const isLoadingExport = ref<boolean>(false)
const handleExport = async () => {
  isLoadingExport.value = true
  try {
    downloadFile(await getPoliciesExportCSV(filters), `policies_export.csv`)
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Unknown error occurred'
    NotifyFail(message)
  } finally {
    isLoadingExport.value = false
  }
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="() => refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <!-- Filters -->
    <div class="flex gap-3">
      <RcCustomerSelect v-if="isASICWAppUser" :disabled="isFetching" class="w-56" @change="onCustomerChange" />
      <el-input
        v-model="groupFragment"
        :disabled="isFetching"
        clearable
        class="w-56"
        placeholder="Group"
        @input="searchGroup"
      />
      <el-input
        v-model="lookupFragment"
        clearable
        class="w-56"
        :disabled="isFetching"
        placeholder="Policy #, Miner SN or SKU"
        :prefix-icon="glassIcon"
        @input="searchFragment"
      />
      <el-select v-model="filters.status" :disabled="isFetching" class="w-56" placeholder="Status" clearable>
        <el-option v-for="st of PolicyStatus" :key="st" :label="st" :value="st" />
      </el-select>

      <el-button
        v-if="isASICWOperationalUser || isASICWViewerUser"
        class="ml-auto"
        :disabled="isLoadingExport"
        @click="handleExport"
        >Export</el-button
      >
    </div>

    <el-table v-loading="isRefetching" :data="policiesData?.data" @selection-change="onSelect">
      <template #empty>
        <RlEmptyDataset title="No policies found." />
      </template>

      <!-- Selection -->
      <el-table-column v-if="isASICWOperationalUser" type="selection" width="55" />

      <!-- Customer -->
      <el-table-column v-if="isASICWAppUser" label="Customer" prop="customer.name" min-width="160" />

      <!-- Group Id -->
      <el-table-column label="Group" prop="group_number" min-width="160" />

      <!-- Policy # -->
      <el-table-column label="Policy #" prop="number" min-width="160" />

      <!-- Miner SN -->
      <el-table-column label="Miner SN" prop="insured_miner.sn" min-width="184" />

      <!-- SKU with tooltip -->
      <el-table-column label="SKU" min-width="136">
        <template #default="{ row }">
          <el-tooltip>
            <template #content>
              <div>
                <p>Environment: {{ row.insured_miner.facility_type }}</p>
                <p>Cooling method: {{ row.insured_miner.cooling_type }}</p>
                <p>3rd party software: {{ row.insured_miner.third_party_firmware_used ? 'Yes' : 'No' }}</p>
                <p>Overclocking miners: {{ row.insured_miner.overclocking ? 'Yes' : 'No' }}</p>
              </div>
            </template>
            <label>{{ row.sku }}</label>
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Location -->
      <el-table-column label="Location" prop="site.name" min-width="160" />

      <!-- Wholesale Cost -->
      <el-table-column
        v-if="isASICWAppUser"
        label="Wholesale Cost"
        prop="wholesale_cost"
        :formatter="currencyFormatter"
        min-width="96"
      />

      <!-- Sale Price -->
      <el-table-column v-if="!isASICWViewerUser" label="Sale Price" prop="sale_price" :formatter="currencyFormatter" />

      <!-- Status badge -->
      <el-table-column label="Status" min-width="132">
        <template #default="{ row }">
          <StatusBadge :status="row.status" />
        </template>
      </el-table-column>

      <!-- Claim button -->
      <el-table-column v-if="isASICWOperationalUser || isCustomer" min-width="112">
        <template #default="{ row }">
          <el-button v-if="isActive(row as IPolicy)" @click="handleClaim(row as IPolicy)">Claim</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="policiesData && policiesData.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="policiesData.total_pages"
      :total="policiesData.total_records"
      :disabled="isRefetching"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
      class="self-end"
    />

    <ClaimCreateDialog v-model="showClaimDialog" :pre-selected-policy="selectedPolicy" />
  </template>
</template>
